import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import LinearGradient from 'react-native-linear-gradient';
import { GlassCard } from '../GlassCard';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface SettingsProfileCardProps {
  displayName: string;
  email?: string;
  propertyUnit?: string;
  initials: string;
  onPress?: () => void;
}

const AvatarGradient: React.FC<{ initials: string }> = React.memo(({ initials }) => {
  const { colors, mode } = useThemeColors();
  const avatarBorderColor = mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  return (
    <View style={[styles.avatarOuter, { borderColor: avatarBorderColor }]}>
      <LinearGradient
        colors={['rgba(71,115,254,0.2)', 'rgba(190,94,200,0.2)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.avatarGradient}
      >
        <Text scale="micro" style={[styles.avatarText, { color: colors.text.tertiary }]}>{initials}</Text>
      </LinearGradient>
    </View>
  );
});

AvatarGradient.displayName = 'AvatarGradient';

export const SettingsProfileCard: React.FC<SettingsProfileCardProps> = React.memo(({
  displayName,
  email,
  propertyUnit,
  initials,
  onPress,
}) => {
  const { colors } = useThemeColors();

  return (
    <GlassCard borderRadius={20} padding={25}>
      <TouchableOpacity
        style={styles.content}
        onPress={onPress}
        activeOpacity={0.7}
        disabled={!onPress}
      >
        <AvatarGradient initials={initials} />
        <View style={styles.textContainer}>
          <Text style={[styles.name, { color: colors.text.primary }]} numberOfLines={2}>{displayName}</Text>
          {email ? (
            <Text style={[styles.email, { color: colors.text.dim }]} numberOfLines={1}>{email}</Text>
          ) : null}
          {propertyUnit ? (
            <Text style={[styles.propertyUnit, { color: colors.text.dimmer }]} numberOfLines={2}>{propertyUnit}</Text>
          ) : null}
        </View>
        {onPress && (
          <View style={[styles.chevronButton, {
            backgroundColor: colors.glass.buttonBg,
            borderColor: colors.glass.buttonBorder,
          }]}>
            <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT
              width={16}
              height={16}
              color={colors.text.dimmer}
            />
          </View>
        )}
      </TouchableOpacity>
    </GlassCard>
  );
});

SettingsProfileCard.displayName = 'SettingsProfileCard';

const styles = StyleSheet.create({
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  avatarOuter: {
    width: 56,
    height: 56,
    borderRadius: 16,
    borderWidth: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarGradient: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 22,
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  textContainer: {
    flex: 1,
    gap: 2,
  },
  name: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
  },
  email: {
    ...REDESIGN_TYPOGRAPHY.small,
    fontWeight: '400',
  },
  propertyUnit: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  chevronButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
